import { useEffect, useRef, useState } from 'react';
import { Music, Disc, Download, Trash2, Check, FileText, Upload, Plus, X, Eye, ChevronDown, ShieldCheck, ShieldX, ShieldAlert } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { ChunkedUploader } from './ChunkedUploader';
import { ChecksumBadges } from './ChecksumBadges';
import { StalledUploadsBanner } from './StalledUploadsBanner';
import { SonyUploadPanel } from './SonyUploadPanel';
import { useAuth } from '../contexts/AuthContext';
import { ordersApi, filesApi } from '../lib/api';
import { toast } from 'sonner';

/**
 * Derive a disc/slot-level status from its individual files. Worst state wins
 * so a collapsed disc row always surfaces failures/mismatches WITHOUT needing
 * to expand it. A successful transfer ("Uploaded") is kept visually NEUTRAL —
 * green is reserved for files whose checksums were actually verified.
 *
 * Precedence: mismatch (red) > verification pending (amber) > verified (green)
 *             > transfer-only uploaded (slate).
 */
export function computeDiscStatus(discFiles) {
    if (!discFiles || discFiles.length === 0) return null;
    let mismatch = 0;
    let pending = 0;
    let verified = 0;
    for (const f of discFiles) {
        // Master file checked against a customer .md5 sidecar.
        if (f.md5_verified === 'mismatch') mismatch += 1;
        else if (f.md5_verified === 'pending') pending += 1;
        else if (f.md5_verified === 'match') verified += 1;

        // A checksum manifest (.md5/.txt) with per-entry verdicts.
        if (f.is_checksum_file) {
            const entries = Array.isArray(f.checksum_entries) ? f.checksum_entries : [];
            if (entries.length > 0) {
                for (const e of entries) {
                    if (e.verdict === 'mismatch') mismatch += 1;
                    else if (e.verdict === 'match') verified += 1;
                    else pending += 1; // pending / undefined => awaiting sibling upload
                }
            } else if (f.checksum_summary === 'mismatch') {
                mismatch += 1;
            } else if (f.checksum_summary === 'pending') {
                pending += 1;
            } else if (f.checksum_summary === 'match' || f.checksum_summary === 'partial') {
                verified += 1;
            }
        }
    }

    if (mismatch > 0) {
        return {
            state: 'mismatch',
            label: `Checksum mismatch (${mismatch})`,
            count: mismatch,
            className: 'text-rose-700',
            Icon: ShieldX,
        };
    }
    if (pending > 0) {
        return {
            state: 'pending',
            label: pending > 1 ? `Verification pending (${pending})` : 'Verification pending',
            count: pending,
            className: 'text-amber-600',
            Icon: ShieldAlert,
        };
    }
    if (verified > 0) {
        return {
            state: 'verified',
            label: 'Verified',
            count: verified,
            className: 'text-green-600',
            Icon: ShieldCheck,
        };
    }
    return {
        state: 'uploaded',
        label: 'Uploaded',
        count: discFiles.length,
        className: 'text-slate-500',
        Icon: Check,
    };
}


/**
 * Whether a file is a PDF that supports the customer-facing "Preview" action.
 * We key off the content_type first, then fall back to the extension so older
 * upload records without explicit MIME info still light up.
 */
const isPreviewablePdf = (file) => {
    if (!file) return false;
    const ct = (file.content_type || '').toLowerCase();
    if (ct === 'application/pdf') return true;
    const name = (file.original_filename || '').toLowerCase();
    return name.endsWith('.pdf');
};

/**
 * Opens an inline-disposition presigned URL for the given file in a new tab.
 * Shows a toast on failure. Used by the "Preview" button next to "Download"
 * on customer-side PDF rows.
 */
const handlePreviewFile = async (file) => {
    try {
        await filesApi.previewInline(file.id);
    } catch (err) {
        toast.error(err?.message || 'Failed to open preview');
    }
};

/**
 * UploadedFileRow — compact single row for a permanently-stored uploaded
 * file. Filename + size share one line; the MD5/SHA1 badges (with copy) and
 * verification pills sit on a tight secondary line, and download/delete stay
 * on the right. Density-tuned to ~45% shorter than the old two-line row.
 * NOTE: active upload / finalization progress cards are rendered by
 * <ChunkedUploader> and are intentionally NOT routed through this — only
 * finished, persisted files use this compact row.
 */
const UploadedFileRow = ({
    file,
    Icon = Music,
    iconColor = 'text-blue-500',
    formatFileSize,
    onDownload,
    onDelete,
    canDeleteFiles,
    rowTestId,
    previewTestId,
}) => (
    <div
        className="flex items-center justify-between gap-2 px-3 py-1 bg-white rounded-lg border"
        data-testid={rowTestId}
    >
        <div className="flex items-center gap-2.5 min-w-0">
            <Icon className={`h-4 w-4 flex-shrink-0 ${iconColor}`} />
            <div className="min-w-0">
                <div className="flex items-baseline gap-2 min-w-0">
                    <p className="text-sm font-medium text-slate-700 truncate">{file.original_filename}</p>
                    <span className="text-xs text-muted-foreground flex-shrink-0">{formatFileSize(file.size)}</span>
                </div>
                <ChecksumBadges file={file} />
            </div>
        </div>
        <div className="flex items-center gap-0.5 flex-shrink-0">
            {isPreviewablePdf(file) && (
                <Button variant="ghost" size="sm" onClick={() => handlePreviewFile(file)} className="h-7 w-7 p-0" title="Preview PDF" data-testid={previewTestId}>
                    <Eye className="h-4 w-4" />
                </Button>
            )}
            <Button variant="ghost" size="sm" onClick={() => onDownload(file)} className="h-7 w-7 p-0" title="Download">
                <Download className="h-4 w-4" />
            </Button>
            {canDeleteFiles && (
                <Button variant="ghost" size="sm" onClick={() => onDelete(file.id)} className="h-7 w-7 p-0 text-red-500 hover:text-red-600">
                    <Trash2 className="h-4 w-4" />
                </Button>
            )}
        </div>
    </div>
);

/**
 * MasterFilesCard
 *
 * Pure presentational + slot/upload-handler container for the "Audio Master / Master Files"
 * Card on /orders/:id/files. Extracted from OrderFilesPage so the parent stays under control
 * and to prevent the kind of hook-misplacement bug that caused a runtime crash earlier today
 * (a hook accidentally bound inside a sub-component referencing parent state).
 *
 * No functional change — this is a mechanical refactor:
 *   • Vinyl Side A/B + Tracklist + extra master slots (editable labels)
 *   • CD index-based "Disc N" labels (static span, auto-init Disc 1)
 *   • DVD/Blu-ray default "Production Master" uploader + extra "Disc N" slots (editable)
 *
 * The parent owns `order` and `files` state and refreshes via `onRefresh`.
 * Slot mutations update parent state through `setOrder` / `setFiles` setters so the
 * parent's other sections (artwork, status, etc.) keep seeing the latest values.
 *
 * Required props:
 *   • order               — parent order document (must contain id + product_type + master_slots)
 *   • files               — flat array of all order files
 *   • orderId             — pulled from parent (used by ChunkedUploader/filesApi)
 *   • canUploadFiles      — boolean (parent permission gate)
 *   • canDeleteFiles      — boolean (parent permission gate)
 *   • isUploading         — boolean for tracklist file <input> disabled state
 *   • setIsUploading      — setter for tracklist immediate-upload flow
 *   • setOrder            — parent state setter (slot mutations)
 *   • setFiles            — parent state setter (file deletions on slot removal)
 *   • onRefresh           — parent fetchData() — called after upload completion
 *   • formatFileSize      — utility re-used from parent (no need to duplicate)
 *   • handleDownloadFile  — delegated to parent so download path stays single-source
 *   • handleDeleteFile    — delegated to parent (also used by Artwork section)
 */
/**
 * MasterFilesOuterShell
 *
 * Presentational wrapper that gives <MasterFilesCard> either its own
 * <Card> shell (default legacy layout) or a bare padded div (when
 * embedded inside <CollapsibleProductionSection>, whose Card already
 * owns the surrounding border/shadow). Defined at module scope — NOT
 * inside MasterFilesCard — so React sees a stable component type and
 * never tears down the wrapped upload state on re-render.
 */
const MasterFilesOuterShell = ({ embedded, isSimpleMedia, audioFilesCount, children }) => (
    embedded ? (
        <div
            className="px-6 pb-6 pt-4 space-y-4"
            data-testid="master-files-embedded-body"
        >
            {children}
        </div>
    ) : (
        <Card className="mb-6">
            <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                    <div>
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Music className="h-5 w-5 text-blue-500" />
                            {isSimpleMedia ? 'Master Files' : 'Audio Master'}
                        </CardTitle>
                        <CardDescription>
                            {isSimpleMedia
                                ? 'Upload your production master'
                                : 'Upload audio master files for your vinyl production'}
                        </CardDescription>
                    </div>
                    {!isSimpleMedia && (
                        <span className="text-sm font-medium text-slate-500" data-testid="audio-status">
                            {audioFilesCount} of 2 uploaded
                        </span>
                    )}
                </div>
            </CardHeader>
            <CardContent className="space-y-4">
                {children}
            </CardContent>
        </Card>
    )
);

export const MasterFilesCard = ({
    order,
    files,
    orderId,
    canUploadFiles,
    canDeleteFiles,
    isUploading,
    setIsUploading,
    setOrder,
    setFiles,
    onRefresh,
    formatFileSize,
    handleDownloadFile,
    handleDeleteFile,
    // Reports aggregate active-upload state (uploading/finalizing) to the
    // parent so the enclosing Master Files section can stay expanded.
    onActiveUploadsChange,
    // When rendered inside <CollapsibleProductionSection> the wrapper
    // already provides its own Card shell + header, so we skip our own
    // to avoid a nested-card border. Pure presentation — no functional
    // change.
    embeddedInCollapsible = false,
}) => {
    const { isAdmin } = useAuth();
    // Derived state from props
    const isSimpleMedia = ['cd', 'dvd', 'bluray'].includes(order?.product_type);
    const isCd = order?.product_type === 'cd';
    const isDvd = order?.product_type === 'dvd';
    const isBluray = order?.product_type === 'bluray';
    // DVD and Blu-ray use the same disc-with-format-and-layers UI shape, just
    // with different format option labels.
    const isLayeredDisc = isDvd || isBluray;

    // Disc-format option lists (only the ID is persisted; UI maps to a label).
    const formatOptions = isDvd
        ? [
              { id: 'dvd5', label: 'DVD 5' },
              { id: 'dvd9', label: 'DVD 9' },
          ]
        : isBluray
        ? [
              { id: 'bd25', label: 'BD 25' },
              { id: 'bd50', label: 'BD 50' },
          ]
        : [];
    // Formats that need TWO upload boxes (one per layer)
    const DUAL_LAYER_FORMATS = new Set(['dvd9', 'bd50']);

    const sideAFile = files.find((f) => f.category === 'audio_side_a');
    const sideBFile = files.find((f) => f.category === 'audio_side_b');
    const tracklistFiles = files.filter((f) => f.category === 'tracklist');
    // Files uploaded in the legacy single-bucket "audio" category before per-side categories existed
    const legacyAudioFiles = files.filter((f) => f.category === 'audio');
    // For simple media orders (CD/DVD/Blu-ray), get ALL files with audio_side_a category
    const simpleMediaMasterFiles = files.filter((f) => f.category === 'audio_side_a');

    const audioFilesCount = isSimpleMedia
        ? simpleMediaMasterFiles.length
        : (sideAFile ? 1 : 0) + (sideBFile ? 1 : 0);

    // Master slot mutations (live inside this component; parent state updated via props)
    const handleAddMasterSlot = async () => {
        try {
            const res = await ordersApi.addMasterSlot(orderId, {});
            setOrder((prev) => ({ ...prev, master_slots: res.data.master_slots }));
            toast.success(`Added "${res.data.created_slot.label}"`);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to add master slot');
        }
    };

    const handleRenameMasterSlot = async (slotId, label) => {
        const trimmed = (label || '').trim();
        if (!trimmed) return;
        try {
            const res = await ordersApi.updateMasterSlot(orderId, slotId, { label: trimmed });
            setOrder((prev) => ({ ...prev, master_slots: res.data.master_slots }));
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to rename slot');
        }
    };

    // Update the disc format on a slot (DVD / Blu-ray only). Switching from a
    // dual-layer format (dvd9 / bd50) to a single-layer one (dvd5 / bd25) will
    // also drop any orphaned layer-1 file on the server side.
    const handleSetSlotFormat = async (slotId, newFormat) => {
        try {
            const res = await ordersApi.updateMasterSlot(orderId, slotId, { format: newFormat });
            setOrder((prev) => ({ ...prev, master_slots: res.data.master_slots }));
            if (res.data.deleted_layer1_files) {
                // Reflect the cleanup in the local files cache so the UI doesn't
                // show stale layer-1 entries until the next refetch.
                setFiles((prev) => prev.filter((f) => f.category !== `master_slot_${slotId}_layer1`));
                toast.info(`Format changed — ${res.data.deleted_layer1_files} layer-1 file removed`);
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to change format');
        }
    };

    // Persist a manual override for the disc's sub-catalogue number. The
    // backend validates + normalises the string, and an empty string clears
    // the override so the auto-generated default takes over again.
    const handleSetSlotSubCatalogue = async (slotId, value) => {
        try {
            const res = await ordersApi.updateMasterSlot(orderId, slotId, {
                sub_catalogue_number: value ?? '',
            });
            setOrder((prev) => ({ ...prev, master_slots: res.data.master_slots }));
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update catalogue number');
        }
    };

    const handleDeleteMasterSlot = async (slotId, slotLabel) => {
        if (!window.confirm(`Remove "${slotLabel}"? Any uploaded file in this slot will also be deleted.`)) return;
        try {
            const res = await ordersApi.deleteMasterSlot(orderId, slotId);
            setOrder((prev) => ({ ...prev, master_slots: res.data.master_slots }));
            // Also drop any cached file from local state (covers both legacy bucket and per-layer categories)
            setFiles((prev) => prev.filter((f) =>
                f.category !== `master_slot_${slotId}` &&
                f.category !== `master_slot_${slotId}_layer0` &&
                f.category !== `master_slot_${slotId}_layer1`,
            ));
            toast.success(
                res.data.deleted_files
                    ? `Removed slot and deleted ${res.data.deleted_files} file`
                    : 'Slot removed',
            );
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to remove slot');
        }
    };

    // Tracklist immediate (small file) upload — only used by the Vinyl branch
    // Mirrors the original parent helper EXACTLY (filesApi.upload(orderId, category, file)).
    const handleImmediateUpload = async (file, category) => {
        if (!file) return;
        setIsUploading(true);
        try {
            const response = await filesApi.upload(orderId, category, file);
            setFiles((prev) => [response.data, ...prev]);
            toast.success('File uploaded');
            onRefresh();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to upload file');
        } finally {
            setIsUploading(false);
        }
    };

    // Auto-initialise the first "Disc 1" slot for CD / DVD / Blu-ray orders
    // that don't have one yet, so the user never sees a separate untyped
    // "default" upload box. Vinyl keeps the fixed Side A / Side B layout.
    // Uses the parent-supplied `canUploadFiles` gate so completed/shipped/cancelled
    // orders never silently spawn new slots.
    const autoInitDiscRef = useRef(false);
    useEffect(() => {
        const productType = order?.product_type;
        const isDiscOrder = productType === 'cd' || productType === 'dvd' || productType === 'bluray';
        const slotsLen = (order?.master_slots || []).length;
        const legacyCount = (files || []).filter((f) => f.category === 'audio_side_a').length;
        if (
            !autoInitDiscRef.current &&
            isDiscOrder &&
            order?.id &&
            slotsLen === 0 &&
            legacyCount === 0 &&
            canUploadFiles
        ) {
            autoInitDiscRef.current = true;
            handleAddMasterSlot();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [order?.id, order?.product_type, order?.master_slots?.length, files?.length, canUploadFiles]);

    // ─── Per-disc collapsibility (multi-disc orders) ─────────────────
    // Pure UI state so users navigating an order with many discs can
    // fold each disc away and focus on the one they're working with.
    // NO backend or upload logic changes: the wrapped file list +
    // uploader stay mounted, we only toggle their visibility.
    //
    // Default rule: expand only the first slot that has no uploaded
    // file yet ("the disc the user is currently working on"). If every
    // slot has at least one file, all discs start collapsed. A manual
    // toggle from the user overrides the default for that slot for the
    // rest of the session.
    const [manualSlotOpenState, setManualSlotOpenState] = useState({});
    // Per-category active-upload flags reported by each <ChunkedUploader>.
    // Keyed by the uploader's `category` (e.g. master_slot_<id>,
    // master_slot_<id>_layer0, audio_side_a). Used to (a) keep the owning
    // Disc expanded while it has work in flight and (b) bubble an aggregate
    // "any master upload active" signal up to the Master Files section.
    const [activeCategories, setActiveCategories] = useState({});

    // Stable-enough handler passed to every uploader. Records the category's
    // active state and, on the rising edge, pins the owning Disc open so the
    // in-progress upload can't be hidden. The pin is written into
    // manualSlotOpenState so it STICKS after the upload finishes — the 2.5s
    // completed-entry removal (which flips active back to false) can no longer
    // collapse the Disc. Users can still collapse it manually afterwards.
    const handleUploaderActive = (slotId, category, active) => {
        setActiveCategories((prev) => {
            if (!!prev[category] === active) return prev;
            return { ...prev, [category]: active };
        });
        if (active && slotId) {
            setManualSlotOpenState((prev) => (prev[slotId] === true ? prev : { ...prev, [slotId]: true }));
        }
    };

    // Bubble aggregate active state to the parent so the Master Files section
    // stays expanded during uploads. Ref-backed to tolerate an inline parent
    // callback without retriggering the effect.
    const anyMasterUploadActive = Object.values(activeCategories).some(Boolean);
    const onActiveUploadsChangeRef = useRef(onActiveUploadsChange);
    onActiveUploadsChangeRef.current = onActiveUploadsChange;
    useEffect(() => {
        if (typeof onActiveUploadsChangeRef.current === 'function') {
            onActiveUploadsChangeRef.current(anyMasterUploadActive);
        }
    }, [anyMasterUploadActive]);

    const slotHasActiveUpload = (slot) => {
        if (!slot) return false;
        return (
            !!activeCategories[`master_slot_${slot.id}`]
            || !!activeCategories[`master_slot_${slot.id}_layer0`]
            || !!activeCategories[`master_slot_${slot.id}_layer1`]
        );
    };
    const slotHasAnyFile = (slot) => {
        if (!slot) return false;
        return (files || []).some(
            (f) => f.category === `master_slot_${slot.id}`
                || f.category === `master_slot_${slot.id}_layer0`
                || f.category === `master_slot_${slot.id}_layer1`,
        );
    };
    const slotFileCount = (slot) => (
        (files || []).filter(
            (f) => f.category === `master_slot_${slot.id}`
                || f.category === `master_slot_${slot.id}_layer0`
                || f.category === `master_slot_${slot.id}_layer1`,
        ).length
    );
    const firstEmptySlotId = (() => {
        const slots = order?.master_slots || [];
        const empty = slots.find((s) => !slotHasAnyFile(s));
        return empty ? empty.id : null;
    })();
    const isSlotOpen = (slot) => {
        const slots = order?.master_slots || [];
        // Single-slot orders are never collapsed — collapsing a single
        // card doesn't help navigation and would hide the uploader.
        if (slots.length <= 1) return true;
        // An active (uploading / finalizing) Disc is always expanded so
        // in-flight work is never hidden, regardless of manual/default state.
        if (slotHasActiveUpload(slot)) return true;
        if (Object.prototype.hasOwnProperty.call(manualSlotOpenState, slot.id)) {
            return manualSlotOpenState[slot.id];
        }
        // Default: only the first empty disc is expanded. When every
        // disc already has files we collapse them all so the section
        // header + per-disc summary is what the user sees first.
        return firstEmptySlotId === slot.id;
    };
    const toggleSlotOpen = (slot) => {
        setManualSlotOpenState((prev) => ({
            ...prev,
            [slot.id]: !isSlotOpen(slot),
        }));
    };

    return (
        <MasterFilesOuterShell
            embedded={embeddedInCollapsible}
            isSimpleMedia={isSimpleMedia}
            audioFilesCount={audioFilesCount}
        >
            {/* Stalled chunked uploads (status="uploading" older than 10 min) — surfaced
               so a half-finished IMAGE.DAT-style file doesn't silently disappear. */}
            <StalledUploadsBanner orderId={orderId} refreshKey={files?.length} onRefresh={onRefresh} />
            {/* Sony master upload (Phase 1) — admin-only. Builds/validates the
                Sony job payload the future EC2 worker will consume. Placed
                here (Master section) because the action uploads MASTER FILES,
                not PO metadata. */}
            {isAdmin && <SonyUploadPanel orderId={orderId} />}
                {isSimpleMedia ? (
                    isCd ? (
                        /* CD orders: NO separate "default" uploader. All masters live in
                           master_slots and are labeled "Disc 1", "Disc 2", … by index.
                           Legacy audio_side_a files (from older CD orders) are shown as
                           read-only legacy entries below. */
                        <div className="space-y-4">
                            {/* Legacy default-bucket files — shown only if older orders had them */}
                            {(simpleMediaMasterFiles.length > 0 || legacyAudioFiles.length > 0) && (
                                <div className="border rounded-lg p-4 bg-amber-50/50 border-amber-200">
                                    <h4 className="font-medium text-slate-700 flex items-center gap-2 mb-2 text-sm">
                                        <Disc className="h-4 w-4 text-amber-600" />
                                        Legacy uploads
                                        <span className="text-xs text-amber-700 font-normal">
                                            (uploaded before disc-numbered slots)
                                        </span>
                                    </h4>
                                    <div className="space-y-2">
                                        {[...simpleMediaMasterFiles, ...legacyAudioFiles].map((file) => (
                                            <UploadedFileRow
                                                key={file.id}
                                                file={file}
                                                formatFileSize={formatFileSize}
                                                onDownload={handleDownloadFile}
                                                onDelete={handleDeleteFile}
                                                canDeleteFiles={canDeleteFiles}
                                                previewTestId="file-preview-button"
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        /* DVD / Blu-ray: like CD, no separate "default" uploader. Each disc
                           lives in master_slots and renders a format selector (DVD 5/9 or
                           BD 25/50) that controls how many layer-uploads appear inside.
                           Legacy audio_side_a files (from older orders) are surfaced as
                           read-only legacy entries below. */
                        <div className="space-y-4">
                            {(simpleMediaMasterFiles.length > 0 || legacyAudioFiles.length > 0) && (
                                <div className="border rounded-lg p-4 bg-amber-50/50 border-amber-200">
                                    <h4 className="font-medium text-slate-700 flex items-center gap-2 mb-2 text-sm">
                                        <Disc className="h-4 w-4 text-amber-600" />
                                        Legacy uploads
                                        <span className="text-xs text-amber-700 font-normal">
                                            (uploaded before per-disc format slots)
                                        </span>
                                    </h4>
                                    <div className="space-y-2">
                                        {[...simpleMediaMasterFiles, ...legacyAudioFiles].map((file) => (
                                            <UploadedFileRow
                                                key={file.id}
                                                file={file}
                                                formatFileSize={formatFileSize}
                                                onDownload={handleDownloadFile}
                                                onDelete={handleDeleteFile}
                                                canDeleteFiles={canDeleteFiles}
                                                previewTestId="file-preview-button"
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )
                ) : (
                    /* Vinyl Audio Masters - Side A / Side B */
                    <>
                        {/* Side A */}
                        <div className="border rounded-lg p-4 bg-slate-50/50">
                            <div className="flex items-center justify-between mb-2">
                                <h4 className="font-medium text-slate-700 flex items-center gap-2">
                                    <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-xs flex items-center justify-center font-bold">A</span>
                                    Side A
                                </h4>
                                {sideAFile && (
                                    <span className="text-xs text-green-600 font-medium flex items-center gap-1">
                                        <Check className="h-3 w-3" /> Uploaded
                                    </span>
                                )}
                            </div>
                            {sideAFile ? (
                                <UploadedFileRow
                                    file={sideAFile}
                                    formatFileSize={formatFileSize}
                                    onDownload={handleDownloadFile}
                                    onDelete={handleDeleteFile}
                                    canDeleteFiles={canDeleteFiles}
                                />
                            ) : canUploadFiles ? (
                                <ChunkedUploader
                                    orderId={orderId}
                                    category="audio_side_a"
                                    onComplete={onRefresh}
                                    onMD5AutoRetryComplete={onRefresh}
                                    onActiveChange={(active) => handleUploaderActive(null, 'audio_side_a', active)}
                                    accept=".wav"
                                    label="Upload Side A (WAV)"
                                    description="Large files supported (up to 100GB) • Resumable uploads"
                                    disabled={!canUploadFiles}
                                />
                            ) : (
                                <div className="text-center p-4 bg-slate-100 rounded-lg">
                                    <p className="text-sm text-slate-400">No file uploaded</p>
                                </div>
                            )}
                        </div>

                        {/* Side B */}
                        <div className="border rounded-lg p-4 bg-slate-50/50">
                            <div className="flex items-center justify-between mb-2">
                                <h4 className="font-medium text-slate-700 flex items-center gap-2">
                                    <span className="w-6 h-6 rounded-full bg-purple-100 text-purple-600 text-xs flex items-center justify-center font-bold">B</span>
                                    Side B
                                </h4>
                                {sideBFile && (
                                    <span className="text-xs text-green-600 font-medium flex items-center gap-1">
                                        <Check className="h-3 w-3" /> Uploaded
                                    </span>
                                )}
                            </div>
                            {sideBFile ? (
                                <UploadedFileRow
                                    file={sideBFile}
                                    iconColor="text-purple-500"
                                    formatFileSize={formatFileSize}
                                    onDownload={handleDownloadFile}
                                    onDelete={handleDeleteFile}
                                    canDeleteFiles={canDeleteFiles}
                                />
                            ) : canUploadFiles ? (
                                <ChunkedUploader
                                    orderId={orderId}
                                    category="audio_side_b"
                                    onComplete={onRefresh}
                                    onMD5AutoRetryComplete={onRefresh}
                                    onActiveChange={(active) => handleUploaderActive(null, 'audio_side_b', active)}
                                    accept=".wav"
                                    label="Upload Side B (WAV)"
                                    description="Large files supported (up to 100GB) • Resumable uploads"
                                    disabled={!canUploadFiles}
                                />
                            ) : (
                                <div className="text-center p-4 bg-slate-100 rounded-lg">
                                    <p className="text-sm text-slate-400">No file uploaded</p>
                                </div>
                            )}
                        </div>
                    </>
                )}

                {/* Extra Master Slots — manual additions today, will be auto-generated from order specs in the future */}
                {(order?.master_slots || []).length > 0 && (
                    <div className="space-y-4 pt-2">
                        {order.master_slots.map((slot, idx) => {
                            // For CD orders the label is ALWAYS index-based ("Disc 1", "Disc 2", …).
                            // The DB-stored label is ignored to keep numbering stable and
                            // tied to position even after reorders or deletions.
                            // DVD / Blu-ray use the same positional numbering scheme.
                            const positionalDiscOrder = isCd || isDvd || isBluray;
                            const displayLabel = positionalDiscOrder ? `Disc ${idx + 1}` : slot.label;

                            // For DVD / Blu-ray, files live under per-layer categories
                            // (master_slot_<id>_layer0 / _layer1) so the same disc can carry
                            // up to two physical files. CD / vinyl keep the legacy single
                            // bucket category master_slot_<id>.
                            const slotCategory = `master_slot_${slot.id}`;
                            const layer0Category = `master_slot_${slot.id}_layer0`;
                            const layer1Category = `master_slot_${slot.id}_layer1`;
                            // Use filter (not find) so MULTIPLE files uploaded to the same
                            // slot/layer are all rendered — earlier versions showed only the
                            // first match which silently hid subsequent uploads even though
                            // they were persisted in S3 + Mongo.
                            const slotFiles = files.filter((f) => f.category === slotCategory);
                            const layer0Files = files.filter((f) => f.category === layer0Category);
                            const layer1Files = files.filter((f) => f.category === layer1Category);

                            const slotFormat = (slot.format || '').toLowerCase();
                            const isDualLayer = DUAL_LAYER_FORMATS.has(slotFormat);

                            const slotPalette = ['blue', 'purple', 'emerald', 'amber', 'rose', 'cyan'];
                            const colorName = slotPalette[idx % slotPalette.length];
                            const colorClasses = {
                                blue: 'bg-blue-100 text-blue-600',
                                purple: 'bg-purple-100 text-purple-600',
                                emerald: 'bg-emerald-100 text-emerald-600',
                                amber: 'bg-amber-100 text-amber-600',
                                rose: 'bg-rose-100 text-rose-600',
                                cyan: 'bg-cyan-100 text-cyan-600',
                            }[colorName];
                            const textColor = {
                                blue: 'text-blue-500',
                                purple: 'text-purple-500',
                                emerald: 'text-emerald-500',
                                amber: 'text-amber-500',
                                rose: 'text-rose-500',
                                cyan: 'text-cyan-500',
                            }[colorName];

                            // Per-layer rendering helper to avoid duplicating the file/uploader card.
                            // Renders ALL files in the layer (an upload may legitimately consist of
                            // multiple files, e.g. an audio master + a checksum sidecar) and keeps the
                            // uploader available so the user can keep adding more to the same layer.
                            const renderLayer = (layerLabel, filesInLayer, category) => (
                                <div className="space-y-1">
                                    <div className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                                        {layerLabel}
                                    </div>
                                    {filesInLayer.length > 0 && (
                                        <div className="space-y-2">
                                            {filesInLayer.map((file) => (
                                                <UploadedFileRow
                                                    key={file.id}
                                                    file={file}
                                                    iconColor={textColor}
                                                    formatFileSize={formatFileSize}
                                                    onDownload={handleDownloadFile}
                                                    onDelete={handleDeleteFile}
                                                    canDeleteFiles={canDeleteFiles}
                                                    rowTestId={`master-slot-file-${file.id}`}
                                                />
                                            ))}
                                        </div>
                                    )}
                                    {canUploadFiles ? (
                                        <ChunkedUploader
                                            orderId={orderId}
                                            category={category}
                                            onComplete={onRefresh}
                                            onMD5AutoRetryComplete={onRefresh}
                                            onActiveChange={(active) => handleUploaderActive(slot.id, category, active)}
                                            label={filesInLayer.length > 0 ? `Add more to ${layerLabel}` : `Upload ${layerLabel}`}
                                            description="Large files supported (up to 100GB) • Resumable uploads"
                                            disabled={!canUploadFiles}
                                        />
                                    ) : filesInLayer.length === 0 ? (
                                        <div className="text-center p-4 bg-slate-100 rounded-lg">
                                            <p className="text-sm text-slate-400">No file uploaded</p>
                                        </div>
                                    ) : null}
                                </div>
                            );

                            const anyFileUploaded = slotFiles.length > 0 || layer0Files.length > 0 || layer1Files.length > 0;
                            const discStatus = computeDiscStatus([...slotFiles, ...layer0Files, ...layer1Files]);
                            const totalFilesInSlot = slotFileCount(slot);
                            const slotOpen = isSlotOpen(slot);
                            const slotIsCollapsible = (order?.master_slots || []).length > 1;

                            return (
                                <div
                                    key={slot.id}
                                    className="border rounded-lg p-4 bg-slate-50/50"
                                    data-testid={`master-slot-${slot.id}`}
                                >
                                    <div className="flex items-center justify-between mb-2 gap-2">
                                        <div className="flex items-center gap-2 min-w-0 flex-1">
                                            {slotIsCollapsible && (
                                                <button
                                                    type="button"
                                                    onClick={() => toggleSlotOpen(slot)}
                                                    className="p-0.5 -ml-1 rounded hover:bg-slate-200/70 text-slate-500 transition-colors flex-shrink-0"
                                                    aria-expanded={slotOpen}
                                                    aria-label={slotOpen ? `Collapse ${displayLabel}` : `Expand ${displayLabel}`}
                                                    data-testid={`master-slot-toggle-${slot.id}`}
                                                >
                                                    <ChevronDown
                                                        className={`h-4 w-4 transition-transform duration-200 ${slotOpen ? 'rotate-0' : '-rotate-90'}`}
                                                    />
                                                </button>
                                            )}
                                            <span className={`w-6 h-6 rounded-full text-xs flex items-center justify-center font-bold flex-shrink-0 ${colorClasses}`}>
                                                {(displayLabel || '?').trim().charAt(0).toUpperCase() || '?'}
                                            </span>
                                            {positionalDiscOrder ? (
                                                <span
                                                    className="font-medium text-slate-700 px-1 py-0.5 text-sm"
                                                    data-testid={`master-slot-label-${slot.id}`}
                                                >
                                                    {displayLabel}
                                                </span>
                                            ) : (
                                                <input
                                                    type="text"
                                                    defaultValue={slot.label}
                                                    onBlur={(e) => {
                                                        if (e.target.value.trim() && e.target.value.trim() !== slot.label) {
                                                            handleRenameMasterSlot(slot.id, e.target.value);
                                                        } else if (!e.target.value.trim()) {
                                                            e.target.value = slot.label;
                                                        }
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') e.target.blur();
                                                    }}
                                                    className="font-medium text-slate-700 bg-transparent border-b border-transparent hover:border-slate-300 focus:border-slate-400 focus:outline-none px-1 py-0.5 text-sm w-full min-w-0"
                                                    placeholder="Label (e.g. Side C, Disc 2)"
                                                    data-testid={`master-slot-label-${slot.id}`}
                                                />
                                            )}
                                            {slotIsCollapsible && (
                                                <span
                                                    className="text-xs text-slate-500 ml-1 flex-shrink-0"
                                                    data-testid={`master-slot-file-count-${slot.id}`}
                                                >
                                                    · {totalFilesInSlot} file{totalFilesInSlot === 1 ? '' : 's'}
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                            {discStatus && (
                                                <span
                                                    className={`text-xs font-medium flex items-center gap-1 ${discStatus.className}`}
                                                    data-testid={`master-slot-status-${discStatus.state}-${slot.id}`}
                                                    title={discStatus.label}
                                                >
                                                    <discStatus.Icon className="h-3 w-3" /> {discStatus.label}
                                                </span>
                                            )}
                                            {canUploadFiles && (
                                                <button
                                                    type="button"
                                                    onClick={() => handleDeleteMasterSlot(slot.id, displayLabel)}
                                                    className="p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-600 transition-colors"
                                                    title="Remove this master slot"
                                                    data-testid={`master-slot-remove-${slot.id}`}
                                                >
                                                    <X className="h-4 w-4" />
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {/* Collapsible body — hidden (but still
                                        mounted) when the disc is folded, so
                                        in-flight uploads survive a toggle. */}
                                    <div className={slotOpen ? 'block' : 'hidden'} data-testid={`master-slot-body-${slot.id}`}>

                                    {/* Sub-catalogue number — shown only on multi-disc orders so
                                        a customer-facing single-disc release isn't cluttered. The
                                        backend already stores per-slot `sub_catalogue_number` and
                                        auto-derives the default as `{catalogue_number}-{discNumber}`.
                                        We only surface that here so the customer can review/edit
                                        it before submission; the logic is unchanged. */}
                                    {(order?.master_slots || []).length > 1 && (
                                        <div className="flex items-center gap-2 -mt-1 mb-2 pl-8">
                                            <span className="text-xs text-slate-500">Catalogue number:</span>
                                            {canUploadFiles ? (
                                                <input
                                                    type="text"
                                                    defaultValue={slot.sub_catalogue_number || ''}
                                                    placeholder={(() => {
                                                        const mainCat = (order?.catalogue_number || '').trim();
                                                        return mainCat ? `${mainCat}-${idx + 1}` : `Disc ${idx + 1}`;
                                                    })()}
                                                    onBlur={(e) => {
                                                        const v = e.target.value.trim();
                                                        if (v !== (slot.sub_catalogue_number || '').trim()) {
                                                            handleSetSlotSubCatalogue(slot.id, v);
                                                        }
                                                    }}
                                                    onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                                    className="text-xs font-medium text-slate-700 bg-transparent border-b border-transparent hover:border-slate-300 focus:border-slate-400 focus:outline-none px-1 py-0.5 w-44 min-w-0"
                                                    data-testid={`master-slot-sub-cat-${slot.id}`}
                                                />
                                            ) : (
                                                <span
                                                    className="text-xs font-medium text-slate-700 px-1"
                                                    data-testid={`master-slot-sub-cat-${slot.id}`}
                                                >
                                                    {(slot.sub_catalogue_number || '').trim() || (() => {
                                                        const mainCat = (order?.catalogue_number || '').trim();
                                                        return mainCat ? `${mainCat}-${idx + 1}` : `Disc ${idx + 1}`;
                                                    })()}
                                                </span>
                                            )}
                                        </div>
                                    )}

                                    {/* DVD / Blu-ray: format selector + 1 or 2 layer uploaders */}
                                    {isLayeredDisc ? (
                                        <div className="space-y-3">
                                            <div className="flex items-center gap-2 text-sm flex-wrap">
                                                <span className="text-slate-500 font-medium">Format:</span>
                                                <div className="inline-flex rounded-md overflow-hidden border" data-testid={`master-slot-format-${slot.id}`}>
                                                    {formatOptions.map((opt) => {
                                                        const active = slotFormat === opt.id;
                                                        // Lock the format selector once any file is uploaded
                                                        // to this disc — switching DVD 5↔9 or BD 25↔50 would
                                                        // either orphan layer-1 files or hide them behind a
                                                        // layout that no longer matches the persisted data.
                                                        const formatLocked = (layer0Files.length + layer1Files.length) > 0;
                                                        const disabled = !canUploadFiles || (formatLocked && !active);
                                                        return (
                                                            <button
                                                                key={opt.id}
                                                                type="button"
                                                                disabled={disabled}
                                                                onClick={() => {
                                                                    if (!active && !formatLocked) handleSetSlotFormat(slot.id, opt.id);
                                                                }}
                                                                title={formatLocked && !active ? 'Remove uploaded files before changing format' : undefined}
                                                                className={`px-3 py-1 text-xs font-medium transition-colors ${
                                                                    active
                                                                        ? 'bg-slate-700 text-white'
                                                                        : 'bg-white text-slate-600 hover:bg-slate-100'
                                                                } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
                                                                data-testid={`master-slot-format-${slot.id}-${opt.id}`}
                                                            >
                                                                {opt.label}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                                {(layer0Files.length + layer1Files.length) > 0 && (
                                                    <span
                                                        className="text-xs text-slate-500 italic"
                                                        data-testid={`master-slot-format-locked-${slot.id}`}
                                                    >
                                                        Remove uploaded files before changing format.
                                                    </span>
                                                )}
                                            </div>

                                            {renderLayer('Layer 0', layer0Files, layer0Category)}
                                            {isDualLayer && renderLayer('Layer 1', layer1Files, layer1Category)}
                                        </div>
                                    ) : slotFiles.length > 0 || canUploadFiles ? (
                                        /* CD / vinyl extra slot: single bucket — render every file in
                                           the bucket AND (when allowed) keep the uploader so users
                                           can add additional masters / sidecar files. */
                                        <div className="space-y-2">
                                            {slotFiles.map((slotFile) => (
                                                <UploadedFileRow
                                                    key={slotFile.id}
                                                    file={slotFile}
                                                    iconColor={textColor}
                                                    formatFileSize={formatFileSize}
                                                    onDownload={handleDownloadFile}
                                                    onDelete={handleDeleteFile}
                                                    canDeleteFiles={canDeleteFiles}
                                                    rowTestId={`master-slot-file-${slotFile.id}`}
                                                />
                                            ))}
                                            {canUploadFiles && (
                                                <ChunkedUploader
                                                    orderId={orderId}
                                                    category={slotCategory}
                                                    onComplete={onRefresh}
                                                    onMD5AutoRetryComplete={onRefresh}
                                                    onActiveChange={(active) => handleUploaderActive(slot.id, slotCategory, active)}
                                                    label={slotFiles.length > 0 ? `Add more to ${displayLabel}` : `Upload ${displayLabel}`}
                                                    description="Large files supported (up to 100GB) • Resumable uploads"
                                                    disabled={!canUploadFiles}
                                                />
                                            )}
                                        </div>
                                    ) : (
                                        <div className="text-center p-4 bg-slate-100 rounded-lg">
                                            <p className="text-sm text-slate-400">No file uploaded</p>
                                        </div>
                                    )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Add another master button — works for all product types */}
                {canUploadFiles && (
                    <div className="pt-1">
                        <button
                            type="button"
                            onClick={handleAddMasterSlot}
                            className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-slate-300 rounded-lg text-slate-500 hover:text-slate-700 hover:border-slate-400 hover:bg-slate-50 transition-colors text-sm font-medium"
                            data-testid="add-master-slot-btn"
                        >
                            <Plus className="h-4 w-4" />
                            Add another master
                        </button>
                    </div>
                )}

                {/* Tracklist + Legacy audio (Vinyl only) — rendered AFTER all audio masters
                    and the "Add another master" button so the layout reads:
                      Side A → Side B → (extra slots) → [+ Add another master] → Tracklist */}
                {!isSimpleMedia && (
                    <>
                        <div className="border rounded-lg p-4 bg-slate-50/50">
                            <div className="flex items-center justify-between mb-2">
                                <h4 className="font-medium text-slate-700 flex items-center gap-2">
                                    <FileText className="h-4 w-4 text-slate-500" />
                                    Tracklist
                                </h4>
                                <span className="text-xs text-slate-500">
                                    {tracklistFiles.length > 0 ? (
                                        <span className="text-green-600 font-medium flex items-center gap-1">
                                            <Check className="h-3 w-3" /> {tracklistFiles.length} file{tracklistFiles.length > 1 ? 's' : ''}
                                        </span>
                                    ) : 'No tracklist uploaded'}
                                </span>
                            </div>
                            {tracklistFiles.length > 0 && (
                                <div className="space-y-2 mb-3">
                                    {tracklistFiles.map((tf) => (
                                        <UploadedFileRow
                                            key={tf.id}
                                            file={tf}
                                            Icon={FileText}
                                            iconColor="text-slate-500"
                                            formatFileSize={formatFileSize}
                                            onDownload={handleDownloadFile}
                                            onDelete={handleDeleteFile}
                                            canDeleteFiles={canDeleteFiles}
                                        />
                                    ))}
                                </div>
                            )}
                            {canUploadFiles ? (
                                <label className="flex items-center justify-center p-4 border-2 border-dashed rounded-lg cursor-pointer hover:border-slate-300 hover:bg-slate-100/50 transition-colors">
                                    <input
                                        type="file"
                                        accept=".xlsx,.csv,.pdf,.txt,.doc,.docx"
                                        className="hidden"
                                        multiple
                                        disabled={isUploading}
                                        onChange={(e) => {
                                            if (e.target.files?.length) {
                                                Array.from(e.target.files).forEach((f) => handleImmediateUpload(f, 'tracklist'));
                                                e.target.value = '';
                                            }
                                        }}
                                        data-testid="tracklist-input"
                                    />
                                    <div className="text-center">
                                        <Upload className="h-5 w-5 mx-auto text-slate-400 mb-1" />
                                        <span className="text-sm text-slate-500">{tracklistFiles.length > 0 ? 'Add more files' : 'Upload tracklist'} (.xlsx, .csv, .pdf, .txt)</span>
                                    </div>
                                </label>
                            ) : tracklistFiles.length === 0 ? (
                                <div className="text-center p-4 bg-slate-100 rounded-lg">
                                    <p className="text-sm text-slate-400">No tracklist uploaded</p>
                                </div>
                            ) : null}
                        </div>

                        {/* Legacy audio files (if any exist) */}
                        {legacyAudioFiles.length > 0 && (
                            <div className="border-t pt-4 mt-4">
                                <p className="text-xs text-slate-400 mb-2">Previously uploaded audio files:</p>
                                <div className="space-y-2">
                                    {legacyAudioFiles.map((file) => (
                                        <div key={file.id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                                            <div className="flex items-center gap-2 min-w-0">
                                                <Music className="h-4 w-4 text-slate-400" />
                                                <span className="truncate text-slate-600">{file.original_filename}</span>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                {isPreviewablePdf(file) && (
                                                    <Button variant="ghost" size="sm" onClick={() => handlePreviewFile(file)} className="h-7 w-7 p-0" title="Preview PDF">
                                                        <Eye className="h-3 w-3" />
                                                    </Button>
                                                )}
                                                <Button variant="ghost" size="sm" onClick={() => handleDownloadFile(file)} className="h-7 w-7 p-0" title="Download">
                                                    <Download className="h-3 w-3" />
                                                </Button>
                                                {canDeleteFiles && (
                                                    <Button variant="ghost" size="sm" onClick={() => handleDeleteFile(file.id)} className="h-7 w-7 p-0 text-red-500">
                                                        <Trash2 className="h-3 w-3" />
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}
        </MasterFilesOuterShell>
    );
};
